/**
 * Skills Gap Analysis Page
 *
 * AI-powered candidate-to-role matching including:
 * - Run gap analyses for open positions
 * - View candidate match scores
 * - Skills alignment visualization
 * - Community fit assessment
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Play,
  Search,
  Filter,
  Users,
  Briefcase,
  Target,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  X,
  RefreshCw,
  Sparkles,
  MapPin,
  Globe,
  GraduationCap,
  Star,
  BarChart2,
  Calendar,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface AnalysisRun {
  id: number;
  role_id: number;
  role_name: string;
  status: string;
  candidates_analyzed: number;
  matches_found: number;
  run_at: string;
  completed_at: string | null;
  created_by: string;
}

interface CandidateMatch {
  candidate_id: number;
  candidate_name: string;
  candidate_email: string;
  overall_score: number;
  skills_score: number;
  community_fit_score: number;
  geographic_score: number;
  language_score: number;
  education_score: number;
  availability_score: number;
  network_score: number;
  matched_skills: string[];
  missing_skills: string[];
  languages: string[];
  location: string;
  recommendation: string;
}

interface Role {
  id: number;
  title: string;
  role_type: string;
  open_positions: number;
  required_skills: string[];
  service_areas: string[];
}

const SCORE_COLORS = {
  high: 'text-emerald-600 bg-emerald-100',
  medium: 'text-amber-600 bg-amber-100',
  low: 'text-red-600 bg-red-100',
};

const getScoreColor = (score: number) => {
  if (score >= 75) return SCORE_COLORS.high;
  if (score >= 50) return SCORE_COLORS.medium;
  return SCORE_COLORS.low;
};

const getScoreLabel = (score: number) => {
  if (score >= 85) return 'Excellent Match';
  if (score >= 75) return 'Strong Match';
  if (score >= 60) return 'Good Match';
  if (score >= 50) return 'Potential Match';
  return 'Weak Match';
};

export default function WorkforceSkillsGapPage() {
  const { t } = useAppTranslation();
  const [analyses, setAnalyses] = useState<AnalysisRun[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AnalysisRun | null>(null);
  const [analysisResults, setAnalysisResults] = useState<CandidateMatch[]>([]);
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for skills gap page');
      setLoading(false);
      return;
    }

    try {
      const [analysesRes, rolesRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/skills-gap/analyses')),
        authFetch(getWeaveinApiUrl('/api/workforce/roles?has_openings=true')),
      ]);

      if (analysesRes.ok) {
        const data = await analysesRes.json();
        setAnalyses(data.items || []);
      }

      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching skills gap data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysisResults = async (analysisId: number) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/skills-gap/analyses/${analysisId}/results`));

      if (res.ok) {
        const data = await res.json();
        setAnalysisResults(data || []);
      }
    } catch (error) {
      console.error('Error fetching analysis results:', error);
    }
  };

  const handleSelectAnalysis = (analysis: AnalysisRun) => {
    setSelectedAnalysis(analysis);
    fetchAnalysisResults(analysis.id);
  };

  const handleRunAnalysis = async () => {
    if (!selectedRoleId) return;

    setRunningAnalysis(true);
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl('/api/workforce/skills-gap/analyze'), {
        method: 'POST',
        jsonBody: { role_id: selectedRoleId },
      });

      if (res.ok) {
        const data = await res.json();
        setShowRunModal(false);
        setSelectedRoleId(null);
        fetchData();
        if (data.analysis_id) {
          // Select the new analysis
          const newAnalysis = { ...data, id: data.analysis_id };
          handleSelectAnalysis(newAnalysis);
        }
      }
    } catch (error) {
      console.error('Error running analysis:', error);
    } finally {
      setRunningAnalysis(false);
    }
  };

  const stats = {
    totalAnalyses: analyses.length,
    totalMatches: analyses.reduce((sum, a) => sum + (a.matches_found || 0), 0),
    rolesWithOpenings: roles.length,
    recentAnalyses: analyses.filter(a => {
      const runDate = new Date(a.run_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return runDate >= weekAgo;
    }).length,
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Skills"
        title={t('workforce.skillsGap.title', 'Skills Gap Analysis')}
        description={t('workforce.skillsGap.description', 'AI-powered candidate matching for workforce roles')}
        actions={
          <button
            onClick={() => setShowRunModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            <Sparkles className="w-4 h-4" />
            <span>{t('workforce.skillsGap.runAnalysis', 'Run Analysis')}</span>
          </button>
        }
      />
      <PageContainer>
      <div className="space-y-6">

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.skillsGap.stats.totalAnalyses', 'Total Analyses')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalAnalyses}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.skillsGap.stats.matchesFound', 'Matches Found')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalMatches}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.skillsGap.stats.openRoles', 'Open Roles')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.rolesWithOpenings}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">{t('workforce.skillsGap.stats.thisWeek', 'This Week')}</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.recentAnalyses}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Analysis History */}
          <div className="lg:col-span-1 bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border">
              <h2 className="font-semibold text-ink">{t('workforce.skillsGap.analysisHistory', 'Analysis History')}</h2>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-ink-500">{t('workforce.skillsGap.loading', 'Loading...')}</div>
              ) : analyses.length === 0 ? (
                <div className="p-6 text-center">
                  <TrendingUp className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                  <p className="text-ink-500">{t('workforce.skillsGap.noAnalyses', 'No analyses yet')}</p>
                  <button
                    onClick={() => setShowRunModal(true)}
                    className="text-emerald-600 hover:text-emerald-700 font-medium mt-2 text-sm"
                  >
                    {t('workforce.skillsGap.runFirstAnalysis', 'Run your first analysis')}
                  </button>
                </div>
              ) : (
                analyses.map((analysis) => (
                  <div
                    key={analysis.id}
                    onClick={() => handleSelectAnalysis(analysis)}
                    className={`px-6 py-4 cursor-pointer hover:bg-paper ${
                      selectedAnalysis?.id === analysis.id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-ink">{analysis.role_name}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        analysis.status === 'completed'
                          ? 'bg-emerald-100 text-emerald-700'
                          : analysis.status === 'running'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-paper-2 text-ink-700'
                      }`}>
                        {analysis.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-ink-500">
                      <span>{analysis.candidates_analyzed} candidates</span>
                      <span className="font-medium text-emerald-600">
                        {analysis.matches_found} matches
                      </span>
                    </div>
                    <p className="text-xs text-ink-400 mt-1">
                      {new Date(analysis.run_at).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Analysis Results */}
          <div className="lg:col-span-2 bg-surface rounded-xl border border-v2border">
            <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
              <h2 className="font-semibold text-ink">
                {selectedAnalysis ? t('workforce.skillsGap.resultsFor', 'Results: {{roleName}}', { roleName: selectedAnalysis.role_name }) : t('workforce.skillsGap.selectAnalysis', 'Select an Analysis')}
              </h2>
              {selectedAnalysis && (
                <button
                  onClick={() => handleSelectAnalysis(selectedAnalysis)}
                  className="p-2 hover:bg-paper-2 rounded"
                  title={t('workforce.skillsGap.refreshResults', 'Refresh results')}
                >
                  <RefreshCw className="w-4 h-4 text-ink-500" />
                </button>
              )}
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {!selectedAnalysis ? (
                <div className="p-12 text-center">
                  <BarChart2 className="w-12 h-12 text-ink-400 mx-auto mb-3" />
                  <p className="text-ink-500 mb-2">{t('workforce.skillsGap.selectToView', 'Select an analysis from the left to view results')}</p>
                  <p className="text-sm text-ink-400">{t('workforce.skillsGap.orRunNew', 'Or run a new analysis to match candidates')}</p>
                </div>
              ) : analysisResults.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-ink-400 mx-auto mb-3" />
                  <p className="text-ink-500">{t('workforce.skillsGap.noMatches', 'No matching candidates found')}</p>
                  <p className="text-sm text-ink-400 mt-1">{t('workforce.skillsGap.tryAdjusting', 'Try adjusting role requirements or adding more candidates')}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {analysisResults.map((match, index) => (
                    <div key={match.candidate_id} className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-paper-2 flex items-center justify-center">
                              <span className="text-ink-500 font-semibold">
                                {match.candidate_name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index < 3 ? 'bg-amber-400 text-white' : 'bg-paper-2 text-ink-500'
                            }`}>
                              {index + 1}
                            </div>
                          </div>
                          <div>
                            <p className="font-semibold text-ink">{match.candidate_name}</p>
                            <p className="text-sm text-ink-500">{match.candidate_email}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <MapPin className="w-3 h-3 text-ink-400" />
                              <span className="text-xs text-ink-500">{match.location}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(match.overall_score)}`}>
                            {match.overall_score}%
                          </div>
                          <p className="text-xs text-ink-500 mt-1">{getScoreLabel(match.overall_score)}</p>
                        </div>
                      </div>

                      {/* Score Breakdown */}
                      <div className="grid grid-cols-7 gap-2 mb-4">
                        {[
                          { label: 'Skills', score: match.skills_score, icon: Target },
                          { label: 'Community', score: match.community_fit_score, icon: Users },
                          { label: 'Location', score: match.geographic_score, icon: MapPin },
                          { label: 'Language', score: match.language_score, icon: Globe },
                          { label: 'Education', score: match.education_score, icon: GraduationCap },
                          { label: 'Availability', score: match.availability_score, icon: Calendar },
                          { label: 'Network', score: match.network_score, icon: Star },
                        ].map((item) => {
                          const Icon = item.icon;
                          return (
                            <div key={item.label} className="text-center">
                              <div className={`mx-auto w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                                item.score >= 75 ? 'bg-emerald-100' : item.score >= 50 ? 'bg-amber-100' : 'bg-paper-2'
                              }`}>
                                <Icon className={`w-4 h-4 ${
                                  item.score >= 75 ? 'text-emerald-600' : item.score >= 50 ? 'text-amber-600' : 'text-ink-400'
                                }`} />
                              </div>
                              <p className="text-xs text-ink-500">{item.label}</p>
                              <p className="text-xs font-semibold text-ink-700">{item.score}%</p>
                            </div>
                          );
                        })}
                      </div>

                      {/* Skills */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {match.matched_skills.map((skill, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {skill}
                          </span>
                        ))}
                        {match.missing_skills.map((skill, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-700"
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {skill}
                          </span>
                        ))}
                      </div>

                      {/* Languages */}
                      {match.languages && match.languages.length > 0 && (
                        <div className="flex items-center space-x-2 mb-3">
                          <Globe className="w-4 h-4 text-ink-400" />
                          <span className="text-sm text-ink-500">
                            {t('workforce.skillsGap.languages', 'Languages')}: {match.languages.join(', ')}
                          </span>
                        </div>
                      )}

                      {/* Recommendation */}
                      <div className="bg-blue-50 rounded-lg p-3 flex items-start space-x-3">
                        <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">{t('workforce.skillsGap.aiRecommendation', 'AI Recommendation')}</p>
                          <p className="text-sm text-blue-700">{match.recommendation}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-end space-x-3 mt-4">
                        <Link
                          to={`/workforce/candidates/${match.candidate_id}`}
                          className="px-4 py-2 text-ink-700 hover:bg-paper-2 rounded-lg text-sm"
                        >
                          {t('workforce.skillsGap.viewProfile', 'View Profile')}
                        </Link>
                        <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">
                          {t('workforce.skillsGap.scheduleInterview', 'Schedule Interview')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Run Analysis Modal */}
        {showRunModal && (
          <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowRunModal(false)} />
            <div className="relative bg-surface rounded-xl shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
                <h2 className="text-lg font-semibold text-ink">{t('workforce.skillsGap.modal.title', 'Run Skills Gap Analysis')}</h2>
                <button
                  onClick={() => setShowRunModal(false)}
                  className="p-2 hover:bg-paper-2 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-ink-500 mb-4">
                  {t('workforce.skillsGap.modal.description', 'Select a role to analyze candidates against. The AI will score each candidate based on skills alignment, community fit, and other factors.')}
                </p>
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-ink-700">{t('workforce.skillsGap.modal.selectRole', 'Select Role')}</label>
                  <select
                    value={selectedRoleId || ''}
                    onChange={(e) => setSelectedRoleId(Number(e.target.value))}
                    className="w-full px-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">{t('workforce.skillsGap.modal.chooseRole', 'Choose a role...')}</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.title} ({role.open_positions} {t('workforce.skillsGap.modal.open', 'open')})
                      </option>
                    ))}
                  </select>
                </div>
                {selectedRoleId && roles.find(r => r.id === selectedRoleId) && (
                  <div className="mt-4 p-4 bg-paper rounded-lg">
                    <p className="text-sm font-medium text-ink-700 mb-2">{t('workforce.skillsGap.modal.roleRequirements', 'Role Requirements')}:</p>
                    <div className="flex flex-wrap gap-1">
                      {roles.find(r => r.id === selectedRoleId)?.required_skills?.map((skill, i) => (
                        <span key={i} className="px-2 py-0.5 bg-surface border border-v2border rounded text-xs text-ink-500">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-v2border flex justify-end space-x-3">
                <button
                  onClick={() => setShowRunModal(false)}
                  className="px-4 py-2 text-ink-700 hover:bg-paper-2 rounded-lg"
                >
                  {t('workforce.skillsGap.modal.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleRunAnalysis}
                  disabled={!selectedRoleId || runningAnalysis}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {runningAnalysis ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>{t('workforce.skillsGap.modal.analyzing', 'Analyzing...')}</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>{t('workforce.skillsGap.modal.runAnalysis', 'Run Analysis')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </PageContainer>
    </div>
  );
}
